import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { ISlotOption } from '@/entities/slot/types';

const useSlotSelectOption = (t: TFunction<string | string[], undefined>, isExpanded: boolean) => {
  const [slotOptionQuantity, setSlotOptionQuantity] = useState<number>(0);
  const [selectedSlotOptionList, setSelectedSlotOptionList] = useState<ISlotOption[]>([]);

  const handleResetSlotOption = useCallback((): void => {
    setSlotOptionQuantity(0);
    setSelectedSlotOptionList([]);
  }, []);

  useEffect((): void => {
    if (isExpanded) {
      handleResetSlotOption();
    }
  }, [handleResetSlotOption, isExpanded]);

  const handleSelectSlotOption =
    (option: ISlotOption) =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();

      setSlotOptionQuantity(0);

      // 이미 선택된 옵션인지 확인
      const isAlreadySelected =
        selectedSlotOptionList.length > 0 && selectedSlotOptionList[0].idx === option.idx;

      // 이미 선택된 옵션이면 제거, 아니면 새로 선택
      setSelectedSlotOptionList(isAlreadySelected ? [] : [option]);
    };

  const handleUpdateSlotOptionQuantity =
    (type: 'INCREASE' | 'DECREASE') =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      setSlotOptionQuantity(prev => {
        if (type === 'INCREASE' && prev >= selectedSlotOptionList[0].availableCount) {
          toast.error(t('messages.maxQuantity'));
          return prev;
        }
        return type === 'INCREASE' ? prev + 1 : Math.max(0, prev - 1);
      });
    };

  return {
    slotOptionQuantity,
    selectedSlotOptionList,
    handleSelectSlotOption,
    handleResetSlotOption,
    handleUpdateSlotOptionQuantity,
  };
};

export default useSlotSelectOption;
